import React, { useState, useEffect, useRef, useCallback } from 'react';
import useLocalStorage from "use-local-storage";
import { ArrowClockwise, ArrowRepeat, Copy } from 'react-bootstrap-icons'
import { ClientsTreeButtonPanel, getNodeButtons } from './ClientsTreeButtonPanel'
import { findAccountNodeByLoginAndClient, getAccountNode, getClientNodeById, getNodeByKey } from '../../utils/treeFunctions'
import { Button, Dropdown } from 'react-bootstrap'
import context, { IClientsTreeNode } from './context/shared';
import { observer } from 'mobx-react-lite';
import TreeWithFilter from '../Misc/TreeWithWilter';
import { FindDeviceModal } from './forms/FindDeviceForm';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../shared';
import { Skeleton } from '@mui/material';
import MobileClientsList from './MobileClientsList';

interface IClientsTreeProps {
    showTopButtons: boolean;
    selectedKey?: string;
    treeKey: string;
    selectFunc?: (node: IClientsTreeNode) => void;
}
const DOUBLE_CLICK_DELAY = 300; // мс
const PENDING_SCROLL_MAX_RETRIES = 5; // попыток дождаться рендера ноды после раскрытия родителей
const PENDING_SCROLL_RETRY_DELAY = 60; // мс

const ClientsTree = observer((props: IClientsTreeProps) => {
    const { showTopButtons, selectFunc, selectedKey, treeKey } = props;
    const { refreshTree, currentNode, setCurrentNode, newClientId,
        setNewClientId, newAccount, setNewAccount, lastRefreshDate,
        clientsTreeData, isClientsTreeDataLoading,
        pendingScrollNodeKey, setPendingScrollNodeKey } = context;
    const treeContainerRef = useRef<HTMLDivElement>(null);
    const treeViewRef = useRef<HTMLDivElement>(null);

    const [userExpandedKeys, setUserExpandedKeys] = useLocalStorage<string[]>(treeKey, []);

    // сохранение скролла при обновлении данных дерева
    const scrollTopRef = useRef(0);
    const skipScrollRestoreRef = useRef(false);

    // целевой скролл, выполняемый после коммита данных (DOM-механизм)
    const pendingScrollRef = useRef<{ mode: 'center' | 'restore', nodeKey?: string } | null>(null);

    const getTreeViewEl = () => {
        // rsuite: форвардится .rs-tree (overflow:hidden), реально скроллится внутренний [role="tree"]
        return (treeViewRef.current?.querySelector<HTMLDivElement>('[role="tree"]')
            ?? treeContainerRef.current?.querySelector<HTMLDivElement>('[role="tree"]')
            ?? null);
    }

    // раскрыть всех предков ноды (вся цепочка вверх до корня), а не только непосредственного родителя
    const expandParent = (node: IClientsTreeNode) => {
        if (!node.parentNodeId)
            return;
        setUserExpandedKeys(prev => {
            const keys = new Set(prev ?? []);
            let parentId: string | undefined = node.parentNodeId;
            const data = context.clientsTreeData;
            while (parentId) {
                if (keys.has(parentId))
                    break;
                keys.add(parentId);
                const parent = getNodeByKey(parentId, data);
                parentId = parent?.parentNodeId;
            }
            return [...keys];
        });
    }

    const onExpand = (expanded) => {
        setUserExpandedKeys(expanded);
        // снять случайное выделение текста, растянутое браузером после раскрытия ноды
        setTimeout(() => window.getSelection()?.removeAllRanges(), 0);
    }

    // флаг, чтобы выполнить скролл к currentNode только при первой загрузке
    const initialScrollDone = useRef<boolean>(false);

    // резолв сохранённого узла по стабильным идентификаторам;
    // key на сервере позиционный (0-<клиент>[-<логин>]) и может сместиться после перезагрузки
    const resolveSavedNode = (saved: IClientsTreeNode, treeNode: IClientsTreeNode[]): IClientsTreeNode | null => {
        switch (saved.type) {
            case 'client':
                return saved.id ? getClientNodeById(saved.id, treeNode) : null;
            case 'account':
                return (saved.name && saved.parentId)
                    ? findAccountNodeByLoginAndClient(saved.name, saved.parentId, treeNode)
                    : null;
            default: // root и фолбэк для старых записей LS — key у root стабилен ("0")
                return getNodeByKey(saved.key, treeNode);
        }
    }

    const initSelectedNode = (treeNode: IClientsTreeNode[]) => {
        if (!currentNode) {
            if (newClientId) {
                const newClientNode = getClientNodeById(newClientId, treeNode);
                if (!newClientNode)
                    return;

                setNewClientId(null);
                setCurrentNode(newClientNode);
                expandParent(newClientNode);
                pendingScrollRef.current = { mode: 'center', nodeKey: newClientNode.key };
                initialScrollDone.current = true;
            }
            if (newAccount) {
                const newAccountNode = getAccountNode(newAccount, treeNode);
                if (!newAccountNode)
                    return;

                setNewAccount(null);
                setCurrentNode(newAccountNode);
                expandParent(newAccountNode);
                pendingScrollRef.current = { mode: 'center', nodeKey: newAccountNode.key };
                initialScrollDone.current = true;
            }

            return;
        }

        const node = resolveSavedNode(currentNode, treeNode);
        if (node) {
            // обновляем currentNode, чтобы в LS сохранился актуальный key
            setCurrentNode(node)
            expandParent(node)

            // при первой загрузке страницы скроллим к текущему ноду
            if (!initialScrollDone.current) {
                pendingScrollRef.current = { mode: 'center', nodeKey: node.key };
                initialScrollDone.current = true;
            }
        }
    }

    useEffect(() => {
        if (clientsTreeData) {
            initSelectedNode(clientsTreeData);
        }
    }, [clientsTreeData]);

    // скролл после коммита данных/раскрытия: центр к ноде или восстановление позиции
    const pendingScrollRetriesRef = useRef(0);

    const performScroll = () => {
        const treeView = getTreeViewEl();
        if (!treeView)
            return;

        const pending = pendingScrollRef.current;
        if (pending) {
            if (pending.mode === 'center' && pending.nodeKey) {
                const el = treeView.querySelector<HTMLElement>(`[data-key="String_${pending.nodeKey}"]`);
                if (!el) {
                    // нода ещё не отрендерилась (родители не успели раскрыться) — пробуем позже,
                    // чтобы не потерять запрошенный скролл
                    if (pendingScrollRetriesRef.current < PENDING_SCROLL_MAX_RETRIES) {
                        pendingScrollRetriesRef.current += 1;
                        setTimeout(performScroll, PENDING_SCROLL_RETRY_DELAY);
                    } else {
                        pendingScrollRef.current = null;
                        pendingScrollRetriesRef.current = 0;
                    }
                    return;
                }
                pendingScrollRef.current = null;
                pendingScrollRetriesRef.current = 0;
                skipScrollRestoreRef.current = true;

                const viewRect = treeView.getBoundingClientRect();
                const elRect = el.getBoundingClientRect();
                // позиция ноды в контенте с учётом текущего скролла
                const elTopInContent = elRect.top - viewRect.top + treeView.scrollTop;
                const maxScroll = Math.max(0, treeView.scrollHeight - viewRect.height);
                const offset = elTopInContent - viewRect.height / 2 + el.offsetHeight / 2;
                treeView.scrollTop = Math.max(0, Math.min(offset, maxScroll));
            } else if (pending.mode === 'restore') {
                pendingScrollRef.current = null;
                skipScrollRestoreRef.current = true;
                treeView.scrollTop = scrollTopRef.current;
            }
            return;
        }

        // восстановление скролла после обновления данных дерева
        if (!skipScrollRestoreRef.current) {
            treeView.scrollTop = scrollTopRef.current;
        }
        skipScrollRestoreRef.current = false;
    };

    useEffect(() => {
        if (!clientsTreeData)
            return;
        performScroll();
    }, [clientsTreeData, userExpandedKeys]);

    // скролл к ноде: раскрыть родителя и выполнить после коммита (DOM-механизм)
    const scrollToNode = useCallback((node: IClientsTreeNode) => {
        expandParent(node);
        skipScrollRestoreRef.current = true;
        pendingScrollRetriesRef.current = 0;
        pendingScrollRef.current = { mode: 'center', nodeKey: node.key };
    }, [userExpandedKeys])

    // раскрыть родителя и проскроллить к ноде, запрошенной извне (двойной клик в таблицах)
    useEffect(() => {
        if (!pendingScrollNodeKey || !clientsTreeData)
            return;
        setPendingScrollNodeKey(null);
        const node = getNodeByKey(pendingScrollNodeKey, clientsTreeData);
        if (!node)
            return;
        scrollToNode(node);
    }, [pendingScrollNodeKey]);

    const clickTimeout = useRef<NodeJS.Timeout | null>(null);
    const lastClickTime = useRef<number>(0);

    const onNodeClick = (nodeData: IClientsTreeNode) => {
        const now = Date.now();
        if (now - lastClickTime.current < DOUBLE_CLICK_DELAY) {
            // Двойной клик
            lastClickTime.current = 0;
            if (clickTimeout.current) {
                clearTimeout(clickTimeout.current);
                clickTimeout.current = null;
            }
            // Ваш обработчик двойного клика:
            const key = nodeData.key;
            setUserExpandedKeys(prev => {
                if (prev?.includes(key)) {
                    return prev.filter(k => k !== key);
                } else {
                    return [...prev ?? [], key];
                }
            });
            // снять случайное выделение текста, растянутое браузером после раскрытия ноды
            setTimeout(() => window.getSelection()?.removeAllRanges(), 0);
        } else {
            lastClickTime.current = now;
            if (clickTimeout.current) clearTimeout(clickTimeout.current);
            clickTimeout.current = setTimeout(() => {
                lastClickTime.current = 0;
            }, DOUBLE_CLICK_DELAY);
            if (showTopButtons) {
                if (currentNode?.key !== nodeData.key)
                    setCurrentNode(nodeData)
            }
            else if (selectFunc)
                selectFunc(nodeData);

        }
    };

    const [popupPos, setPopupPos] = useState<{ left: number, top: number }>({ left: 0, top: 0 });
    const [showPopup, setShowPopup] = useState(false);

    const hidePopup = () => {
        setShowPopup(false);
        context.setShowRightClickPopup(false);
    }

    useEffect(() => {
        if (!showPopup) return;
        const handleDocClick = (e: MouseEvent) => {
            // Игнорировать клики по открытому модальному окну (bootstrap/react-bootstrap)
            const target = e.target as HTMLElement;
            if (target.closest('.modal') || document.querySelector('.modal.show')) {
                return;
            }

            // Закрыть при клике вне контейнера попапа
            const popupEl = document.querySelector('.treePopupContainer');
            if (popupEl && !popupEl.contains(target)) {
                hidePopup();
            }
        };
        document.addEventListener('mousedown', handleDocClick);
        return () => document.removeEventListener('mousedown', handleDocClick);
    }, [showPopup]);

    const onNodeContextMenu = (nodeData: IClientsTreeNode, event: React.MouseEvent) => {
        event.preventDefault();
        setCurrentNode(nodeData);

        const containerRect = treeContainerRef.current?.getBoundingClientRect();
        const clientX = event.clientX;
        const clientY = event.clientY;
        const left = containerRect ? clientX - containerRect.left : clientX;
        const top = containerRect ? clientY - containerRect.top : clientY;

        setPopupPos({ left, top });
        setShowPopup(true);
        context.setShowRightClickPopup(true);
    }
    const renderHandle = (nodeData: IClientsTreeNode) => {
        return (
            <div onContextMenu={(event) => onNodeContextMenu(nodeData, event)}>{nodeData.label}</div>
        )
    }
    const tree =
        !clientsTreeData && isClientsTreeDataLoading ?
            <Skeleton width="100%" height="100%" />
            :
            <div ref={treeContainerRef} style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
            <TreeWithFilter
                className="clientsTree"
                data={clientsTreeData}
                valueKey="key"
                labelKey="label"
                onSelectItem={onNodeClick}
                onExpand={onExpand}
                renderTreeNode={renderHandle}
                expandItemValues={userExpandedKeys}
                ref={treeViewRef}
                onScroll={(e: React.SyntheticEvent<HTMLDivElement>) => {
                    scrollTopRef.current = e.currentTarget.scrollTop;
                }}
                value={showTopButtons ? currentNode?.key : selectedKey}
            />
                {currentNode ?
                    createPortal(
                        <div className="treePopupContainer"
                            style={{ position: 'absolute', left: popupPos.left, top: popupPos.top, zIndex: 999, display: showPopup ? 'block' : 'none' }}
                            onClick={() => hidePopup()}>
                            <div className='treePopup'
                                style={{
                                    borderRadius: 7,
                                    border: '1px solid var(--border)',
                                    background: 'var(--bg-lighter)',
                                }}>
                                {getNodeButtons(currentNode?.type, true)}
                                <FindDeviceModal showButtonText={true} />
                                <Button
                                    title="Копировать"
                                    onClick={() => {
                                        navigator.clipboard?.writeText(currentNode?.name ?? String(currentNode?.label ?? ''));
                                        hidePopup();
                                    }}
                                    variant="transparent"
                                    className="centerButton">
                                    <Copy />Копировать
                                </Button>
                                <Button
                                    title="Обновить"
                                    onClick={refreshTree}
                                    variant="transparent"
                                    className="centerButton">
                                    <ArrowClockwise />Обновить
                                </Button>
                            </div>
                        </div>, document.body
                    )
                    : null
            }
        </div>    
    
    const autoRefreshOptions = [
        { value: 0, label: "Автообновление: выкл" },
        { value: 5, label: "5 сек" },
        { value: 10, label: "10 сек" },
        { value: 30, label: "30 сек" },
        { value: 60, label: "60 сек" },
    ];
    const currentAutoRefreshValue = context.autoRefreshEnabled ? context.autoRefreshIntervalSec : 0;

    return (
        <>
            {
                useIsMobile() ? <MobileClientsList showTopButtons={showTopButtons} selectFunc={selectFunc} selectedKey={selectedKey} /> :
                
                <div style={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    {
                        showTopButtons ?
                            <div style={{ flex: '0 0 auto' }}>
                                <ClientsTreeButtonPanel
                                />
                            </div>
                            : <></>
                    }
                        {tree}
                        <div style={{
                            display: 'flex', gap: 5, justifyContent: 'space-between', padding: '5px', flex: '0 0 auto'
                        }} >
                            <Button className="lastDataButton centerButton" title="Обновить" onClick={refreshTree} variant="transparent"><ArrowClockwise className='mediumSvg' />
                                <label style={{ marginLeft: ".5rem" }}>{lastRefreshDate}</label>
                            </Button>
                            {showTopButtons &&
                                <Dropdown
                                    className="autoRefreshDropdown dropup"
                                    align={{ sm: 'end' }}
                                >
                                    <Dropdown.Toggle className="autoRefreshToggle" title={currentAutoRefreshValue === 0 ? "Автообновление: выкл" : `Автообновление: ${currentAutoRefreshValue} сек`}>
                                        <ArrowRepeat className="mediumSvg" />
                                        <span className="autoRefreshValue">{currentAutoRefreshValue === 0 ? "x" : currentAutoRefreshValue}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {autoRefreshOptions.map(o =>
                                            <Dropdown.Item
                                                key={o.value}
                                                active={o.value === currentAutoRefreshValue}
                                                onClick={() => {
                                                    if (o.value === 0)
                                                        context.setAutoRefreshEnabled(false);
                                                    else {
                                                        context.setAutoRefreshIntervalSec(o.value);
                                                        context.setAutoRefreshEnabled(true);
                                                    }
                                                }}>
                                                {o.label}
                                            </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            }
                        </div>                            
                </div>
            }
        </>
    )
})
export default ClientsTree;